import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { isAxiosError } from 'axios'
import type { User } from '@/types'
import api, { refreshClient } from '@/services/api'
import i18n, { setLocale } from '@/i18n'
import type { SupportedLocale } from '@/i18n'
import { authFailureCode, readAuthContext, rememberAuthContext, type AuthFlowParams } from '@/utils/authFlow'

// API URL for auth endpoints
const API_URL = import.meta.env.VITE_API_URL || 'https://api.staging.profitjam.io'

// DEV MODE - Check if dev auth bypass is enabled
const DEV_AUTH_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true'

// DEV MODE - Mock user for development
const DEV_MOCK_USER: User = {
  id: 'dev-user-001',
  email: 'dev@profitjam.io',
  name: 'Dev User',
  role: 'admin',
  avatar: undefined
}

// Storage key for user data (tokens are now in HTTP-only cookies)
const USER_KEY = 'auth_user'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(DEV_AUTH_BYPASS ? DEV_MOCK_USER : null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const isAuthChecked = ref(false)
  const errorCode = ref<string | null>(null)
  let authCheckPromise: Promise<boolean> | null = null

  // Computed
  const isAuthenticated = computed(() => {
    if (DEV_AUTH_BYPASS) return true
    return isAuthChecked.value && !!user.value
  })

  const isAdmin = computed(() => {
    if (DEV_AUTH_BYPASS) return true
    return isAuthenticated.value && user.value?.role === 'admin'
  })

  // Actions

  /**
   * Redirect to backend login endpoint
   * Backend will redirect to Auth0 with PKCE
   */
  function loginWithAuth0(params?: AuthFlowParams): void {
    if (DEV_AUTH_BYPASS) {
      console.warn('DEV MODE: Auth bypass enabled')
      return
    }
    rememberAuthContext(params || {})
    const qs = new URLSearchParams(readAuthContext() as Record<string, string>)
    if (params?.reauthenticate) qs.set('reauthenticate', '1')
    const query = qs.toString()
    window.location.href = `${API_URL}/public/auth/login${query ? '?' + query : ''}`
  }

  /**
   * Redirect to backend signup endpoint
   * Backend will redirect to Auth0 with PKCE
   */
  function signupWithAuth0(params?: AuthFlowParams): void {
    if (DEV_AUTH_BYPASS) {
      console.warn('DEV MODE: Auth bypass enabled')
      return
    }
    rememberAuthContext(params || {})
    const qs = new URLSearchParams(readAuthContext() as Record<string, string>)
    const query = qs.toString()
    window.location.href = `${API_URL}/public/auth/signup${query ? '?' + query : ''}`
  }

  /**
   * Check authentication status by calling /auth/me
   * This uses cookies set by the backend
   */
  function checkAuth(): Promise<boolean> {
    if (!authCheckPromise) {
      authCheckPromise = performAuthCheck().finally(() => { authCheckPromise = null })
    }
    return authCheckPromise
  }

  async function performAuthCheck(): Promise<boolean> {
    if (DEV_AUTH_BYPASS) {
      user.value = DEV_MOCK_USER
      isAuthChecked.value = true
      return true
    }

    isLoading.value = true
    error.value = null
    errorCode.value = null

    try {
      const response = await api.get<User>('/public/auth/me', {
        withCredentials: true
      })
      user.value = response.data
      localStorage.setItem(USER_KEY, JSON.stringify(response.data))
      isAuthChecked.value = true

      // Set locale from user's language preference ('auto' is handled reactively in App.vue)
      if (response.data.language && response.data.language !== 'auto') {
        setLocale(response.data.language as SupportedLocale)
      }

      return true
    } catch (authError) {
      let failure = authError
      const refreshed = isAxiosError(authError) && authError.response?.status === 401
        ? await refreshToken() : false
      if (refreshed) {
        try {
          const retryResponse = await api.get<User>('/public/auth/me', {
            withCredentials: true
          })
          user.value = retryResponse.data
          localStorage.setItem(USER_KEY, JSON.stringify(retryResponse.data))
          isAuthChecked.value = true

          if (retryResponse.data.language && retryResponse.data.language !== 'auto') {
            setLocale(retryResponse.data.language as SupportedLocale)
          }

          return true
        } catch (retryError) {
          failure = retryError
        }
      }

      user.value = null
      errorCode.value ||= authFailureCode(failure)
      localStorage.removeItem(USER_KEY)
      isAuthChecked.value = true
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Refresh the access token using the refresh token cookie
   */
  async function refreshToken(): Promise<boolean> {
    if (DEV_AUTH_BYPASS) return true

    try {
      await refreshClient.post('/public/auth/refresh', {}, {
        withCredentials: true
      })
      return true
    } catch (refreshError) {
      errorCode.value = authFailureCode(refreshError)
      // Refresh failed, user needs to re-authenticate
      user.value = null
      localStorage.removeItem(USER_KEY)
      return false
    }
  }

  /**
   * Logout - redirect to backend logout endpoint
   * Backend will clear cookies and redirect to Auth0 logout
   */
  function logout(): void {
    // Clear local state
    user.value = null
    localStorage.removeItem(USER_KEY)
    sessionStorage.clear()

    if (DEV_AUTH_BYPASS) {
      return
    }

    // Redirect to backend logout (which redirects to Auth0 logout)
    window.location.href = `${API_URL}/public/auth/logout`
  }

  /**
   * Set user manually (for updates)
   */
  function setUser(newUser: User | null): void {
    user.value = newUser
    if (newUser) {
      localStorage.setItem(USER_KEY, JSON.stringify(newUser))
    } else {
      localStorage.removeItem(USER_KEY)
    }
  }

  /**
   * Initialize from localStorage (for cached user data)
   * Still need to call checkAuth() to verify with backend
   */
  function initFromStorage(): void {
    if (DEV_AUTH_BYPASS) {
      user.value = DEV_MOCK_USER
      isAuthChecked.value = true
      return
    }

    const storedUser = localStorage.getItem(USER_KEY)
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser)
        user.value = parsed
        // Restore locale from cached user ('auto' is handled reactively in App.vue)
        if (parsed.language && parsed.language !== 'auto') {
          setLocale(parsed.language as SupportedLocale)
        }
      } catch {
        localStorage.removeItem(USER_KEY)
      }
    }
  }

  /**
   * Fetch user from API (alias for checkAuth)
   */
  async function fetchUser(): Promise<void> {
    await checkAuth()
  }

  /**
   * Update user profile
   */
  async function updateProfile(data: { firstName?: string; lastName?: string; language?: string; timeDisplay?: 'workspace' | 'device' }): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.patch('/private/user', data, {
        withCredentials: true
      })
      const member = response.data
      // Backend returns raw member fields; normalize to our User shape
      const updated: User = {
        id: member.id,
        email: member.email,
        name: `${member.firstName || ''} ${member.lastName || ''}`.trim() || member.email,
        role: member.role || 'user',
        language: member.language,
        timeDisplay: member.timeDisplay,
      }
      user.value = updated
      localStorage.setItem(USER_KEY, JSON.stringify(updated))
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : i18n.global.t('stores.auth.failedToUpdateProfile')
      error.value = errorMessage
      throw e
    } finally {
      isLoading.value = false
    }
  }

  // Initialize from storage on store creation
  initFromStorage()

  return {
    // State
    user,
    isLoading,
    error,
    isAuthChecked,
    errorCode,
    // Computed
    isAuthenticated,
    isAdmin,
    // Actions
    loginWithAuth0,
    signupWithAuth0,
    checkAuth,
    refreshToken,
    logout,
    setUser,
    fetchUser,
    updateProfile,
    initFromStorage
  }
})
